import AppHeader from "@saleor/components/AppHeader";
import CardSpacer from "@saleor/components/CardSpacer";
import Container from "@saleor/components/Container";
import LanguageSwitch from "@saleor/components/LanguageSwitch";
import PageHeader from "@saleor/components/PageHeader";
import { ProductVariantTranslationFragment } from "@saleor/fragments/types/ProductVariantTranslationFragment";
import { commonMessages, sectionNames } from "@saleor/intl";
import { TranslationsEntitiesPageProps } from "@saleor/translations/types";
import React from "react";
import { useIntl } from "react-intl";

import { maybe } from "../../../misc";
import { LanguageCodeEnum } from "../../../types/globalTypes";
import TranslationFields from "../TranslationFields";

export interface TranslationsProductVariantPageProps
  extends TranslationsEntitiesPageProps {
  variant: ProductVariantTranslationFragment;
}

export const fieldNames = {
  name: "name",
  skuName: "skuName",
  fullDescription: "fullDescription",
  shortDescription: "shortDescription",
  describe: "describe",
  USP: "USP",
  seoTitle: "seoTitle",
  seoKeyword: "seoKeyword",
  metadata: "metadata",
  seoDescription: "seoDescription"
};

const TranslationsProductVariantPage: React.FC<TranslationsProductVariantPageProps> = ({
  activeField,
  disabled,
  languageCode,
  languages,
  variant,
  saveButtonState,
  onBack,
  onDiscard,
  onEdit,
  onLanguageChange,
  onSubmit
}) => {
  const intl = useIntl();

  return (
    <Container>
      <AppHeader onBack={onBack}>
        {intl.formatMessage(sectionNames.translations)}
      </AppHeader>
      <PageHeader
        title={intl.formatMessage(
          {
            defaultMessage:
              'Translation Product "{productName}" - {languageCode}',
            description: "header"
          },
          {
            languageCode,
            productName: maybe(() => variant.name, "...")
          }
        )}
      >
        <LanguageSwitch
          currentLanguage={LanguageCodeEnum[languageCode]}
          languages={languages}
          onLanguageChange={onLanguageChange}
        />
      </PageHeader>
      <TranslationFields
        activeField={activeField}
        disabled={disabled}
        initialState={true}
        title={intl.formatMessage(commonMessages.generalInformations)}
        fields={[
          {
            displayName: intl.formatMessage({
              defaultMessage: "Variant Name"
            }),
            name: fieldNames.name,
            translation: maybe(() =>
              variant.translation ? variant.translation.name : null
            ),
            type: "short" as "short",
            value: maybe(() => variant.name)
          },
          {
            displayName: intl.formatMessage({
              defaultMessage: "SKU Name"
            }),
            name: fieldNames.skuName,
            translation: maybe(() =>
              variant.translation ? variant.translation.skuName : null
            ),
            type: "short" as "short",
            value: maybe(() => variant.skuName)
          },
          {
            displayName: intl.formatMessage({
              defaultMessage: "Describe"
            }),
            name: fieldNames.describe,
            translation: maybe(() =>
              variant.translation ? variant.translation.describe : null
            ),
            type: "long" as "long",
            value: maybe(() => variant.describe)
          },
          {
            displayName: intl.formatMessage({
              defaultMessage: "Full Description"
            }),
            name: fieldNames.fullDescription,
            translation: maybe(() =>
              variant.translation ? variant.translation.fullDescription : null
            ),
            type: "long" as "long",
            value: maybe(() => variant.fullDescription)
          },
          {
            displayName: intl.formatMessage({
              defaultMessage: "Short Description"
            }),
            name: fieldNames.shortDescription,
            translation: maybe(() =>
              variant.translation ? variant.translation.shortDescription : null
            ),
            type: "long" as "long",
            value: maybe(() => variant.shortDescription)
          },
          {
            displayName: intl.formatMessage({
              defaultMessage: "MetaData"
            }),
            name: fieldNames.metadata,
            translation: maybe(() =>
              variant.translation ? variant.translation.metadata : null
            ),
            type: "long" as "long",
            value: maybe(() => variant.metadataJson)
          }
        ]}
        saveButtonState={saveButtonState}
        onEdit={onEdit}
        onDiscard={onDiscard}
        onSubmit={onSubmit}
      />
      <CardSpacer />
      <TranslationFields
        activeField={activeField}
        disabled={disabled}
        initialState={true}
        title={intl.formatMessage({
          defaultMessage: "Search Engine Preview"
        })}
        fields={[
          {
            displayName: intl.formatMessage({
              defaultMessage: "Search Engine Title"
            }),
            name: fieldNames.seoTitle,
            translation: maybe(() =>
              variant.translation ? variant.translation.seoTitle : null
            ),
            type: "short" as "short",
            value: maybe(() => variant.seoTitle)
          },
          {
            displayName: intl.formatMessage({
              defaultMessage: "Search Engine Keyword"
            }),
            name: fieldNames.seoKeyword,
            translation: maybe(() =>
              variant.translation ? variant.translation.seoKeyword : null
            ),
            type: "long" as "long",
            value: maybe(() => variant.seoKeyword)
          },
          {
            displayName: intl.formatMessage({
              defaultMessage: "Search Engine Description"
            }),
            name: fieldNames.seoDescription,
            translation: maybe(() =>
              variant.translation ? variant.translation.seoDescription : null
            ),
            type: "long" as "long",
            value: maybe(() => variant.seoDescription)
          },
          {
            displayName: intl.formatMessage({
              defaultMessage: "Variant USP"
            }),
            name: fieldNames.USP,
            translation: maybe(() =>
              variant.translation ? variant.translation.USP : null
            ),
            type: "short" as "short",
            value: maybe(() => variant.USP)
          }
        ]}
        saveButtonState={saveButtonState}
        onEdit={onEdit}
        onDiscard={onDiscard}
        onSubmit={onSubmit}
      />
    </Container>
  );
};
TranslationsProductVariantPage.displayName = "TranslationsProductVariantPage";
export default TranslationsProductVariantPage;
